<template>
    <p>{{ pensionPotValuationText }}</p>

    <p>{{ matchedPaymentsText }}</p>

    <table v-if="fundSplitInfo.matchedYearsInWindow > 0" class="table">
        <thead>
            <tr>
                <th>Matched Ret. Year</th>
                <th class="text-end">Drawdown Amount</th>
                <th class="text-center">Held In {{ fundInfo.matchingIncomeFund.name }}</th>
                <th class="text-end">Matched Amount</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="drawdownPOO, i in props.fundSplitInfo.drawdowns.slice(0, props.fundSplitInfo.matchedYears)" :key="i">
                <td>{{ i + 1 }}</td>
                <td class="text-end font-monospace">{{ formatter.formatCurrency(drawdownPOO.y) }}</td>
                <td class="text-center">{{ getIsMatchedYearInWindowYesNo(i + 1, props.fundSplitInfo.matchedYearsInWindow) }}</td>
                <td class="text-end font-monospace">{{ getMatchedYearAmountAllocToMatchingIncome(i + 1, props.fundSplitInfo.matchedYearsInWindow, drawdownPOO.y) }}</td>
            </tr>
            <tr>
                <td></td>
                <td class="text-end"></td>
                <td class="text-end"></td>
                <td class="text-end fw-bold font-monospace">{{ formatter.formatCurrency(amountAllocatedToMatchingIncomeFund) }}</td>
            </tr>
        </tbody>
    </table>

    <p>{{ lumpSumText}}</p>

    <p>This results in the following fund split:</p>

    <table class="table">
        <thead>
            <tr>
                <th>Fund</th>
                <th class="text-end">Percentage</th>
                <th class="text-end">Amount</th>
            </tr>
        </thead>
        <tbody>
            <tr v-for="fundAllocation, i in fundSplitInfo.allocations" :key="i">
                <td>{{ fundAllocation.fund.name }}</td>
                <td class="text-end font-monospace">{{ `${fundAllocation.percentage.toFixed(1)}%` }}</td>
                <td class="text-end font-monospace">{{ formatter.formatCurrency(fundAllocation.amount) }}</td>
            </tr>
            <tr>
                <td class="text-end"></td>
                <td class="text-end"></td>
                <td class="text-end fw-bold font-monospace">{{ formatter.formatCurrency(props.fundSplitInfo.valueOfPensionPots) }}</td>
            </tr>
        </tbody>
    </table>
</template>

<script setup lang="ts">
import { ref, watch, computed } from "vue";
import FundSplitInfo from '@/guiide/ts/secure/fundSplitInfo';
import { PointOptionsObject } from 'highcharts';
import { formatter } from "@/shared/ts/formatter";
import fundInfo from "@/guiide/ts/funds";
import { singularOrPlural } from "@/guiide/ts/utilities";
import FundAllocation from "@/guiide/ts/fundAllocation";

const props = defineProps({
    fundSplitInfo: {
        type: FundSplitInfo,
        default: new FundSplitInfo(0, 0, 0, false, 0, new Array<PointOptionsObject>(), new Array<FundAllocation>())
    }
});

const trusteeBankAccountAmount = ref(0);
const pensionPotValuationText = ref("");
const matchedPaymentsText = ref("");
const lumpSumText = ref("");

watch(
    () => props.fundSplitInfo,
    (newFundSplitInfo: FundSplitInfo) => {
        trusteeBankAccountAmount.value = newFundSplitInfo.valueOfPensionPots * 0.01;
        pensionPotValuationText.value = generatePensionPotValuationText(newFundSplitInfo);
        matchedPaymentsText.value = generateMatchedPaymentsText(newFundSplitInfo);
        lumpSumText.value = generateLumpSumText(newFundSplitInfo);
    },
    { deep: true }
);

const formattedPensionPotsTotal = computed<string>(() => {
    return formatter.formatCurrency(props.fundSplitInfo.valueOfPensionPots)
});

const amountAllocatedToMatchingIncomeFund = computed<number>(() => {
    return getAmountAllocatedToMatchingIncomeFund(props.fundSplitInfo);
});

function generatePensionPotValuationText(fundSplitInfo: FundSplitInfo): string {
    const currentOrExpectedWord = getCurrentOrExpectedWord(fundSplitInfo.isPresentTime);
    
    let temporalLocation = "";
    if (fundSplitInfo.isPresentTime) {
        temporalLocation = ""
    } else if (fundSplitInfo.yearsToRetirement == 0) {
        temporalLocation = "at retirement ";
    } else {
        const yearsToRetirementWord = singularOrPlural("year", fundSplitInfo.yearsToRetirement);
        temporalLocation = `${fundSplitInfo.yearsToRetirement} ${yearsToRetirementWord} before retirement`;
    }

    return `The ${currentOrExpectedWord} value of your pension pots ${temporalLocation} is ${formattedPensionPotsTotal.value}.`;
}

function generateMatchedPaymentsText(fundSplitInfo: FundSplitInfo): string {
    if (!fundSplitInfo.hasMatchedYears()) {
        return "You have no matched payments.";
    }

    const matchedYearsYearWord = singularOrPlural("year", fundSplitInfo.matchedYears);
    const yearsToRetirementYearWord = singularOrPlural("year", fundSplitInfo.yearsToRetirement);

    if (fundSplitInfo.matchedYearsInWindow == 0) {
        return `You have matched ${fundSplitInfo.matchedYears} ${matchedYearsYearWord} of payments, but ${fundSplitInfo.yearsToRetirement}`
            + ` ${yearsToRetirementYearWord} before retirement none of the matched years are close enough to your retirement date to need`
            + ` any of those payments to be held in the ${fundInfo.matchingIncomeFund.name}. All of the funds to cover your matched years`
            + ` will remain in the ${fundInfo.responsibleGrowthFund.name}.`;
    }

    const matchingIncomeFundAllocAmount = getAmountAllocatedToMatchingIncomeFund(fundSplitInfo);
    let matchingIncomeAmountFormatted = formatter.formatCurrency(matchingIncomeFundAllocAmount);

    if (!fundSplitInfo.allYearsAreMatched()) {
        const matchedYearsWord = fundSplitInfo.matchedYearsInWindow == 1 ? "" : ` ${fundSplitInfo.matchedYearsInWindow}`;
        const comesToATotalText = fundSplitInfo.matchedYearsInWindow == 1 ? "is" : "comes to a total of";
        return `The first ${matchedYearsWord} of your ${fundSplitInfo.matchedYears} matched years will be allocated to`
        + ` the ${fundInfo.matchingIncomeFund.name}, which ${comesToATotalText} ${matchingIncomeAmountFormatted}.`;
    }

    return `All ${fundSplitInfo.matchedYears} matched years will be allocated to the ${fundInfo.matchingIncomeFund.name} which comes to a total of ${matchingIncomeAmountFormatted}.`;
}

function generateLumpSumText(fundSplitInfo: FundSplitInfo): string {
    const currentOrExpectedWord = getCurrentOrExpectedWord(fundSplitInfo.isPresentTime);
    const lumpSumAmountFormatted = formatter.formatCurrency(fundSplitInfo.taxFreeLumpSumAmount);
    return `You are taking a tax free lump sum and have chosen to hold it in the ${fundSplitInfo.taxFreeLumpSumAllocFund.name}.`
    + ` This 25% of your ${currentOrExpectedWord} total pension pots value is ${lumpSumAmountFormatted}.`;
}

function getIsMatchedYearInWindowYesNo(year: number, matchedYearsInWindow: number): string {
    return year <= matchedYearsInWindow ? "Yes" : "No";
}

function getCurrentOrExpectedWord(isPresentTime: boolean): string {
    return isPresentTime ? "current" : "expected";
}

function getMatchedYearAmountAllocToMatchingIncome(year: number, matchedYearsInWindow: number, amount: number): string {
    return year <= matchedYearsInWindow
        ? formatter.formatCurrency(amount)
        : formatter.formatCurrency(0);
}

function getAmountAllocatedToMatchingIncomeFund(fundSplitInfo: FundSplitInfo): number {
    return fundSplitInfo.allocations.filter(a => a.fund.id == fundInfo.matchingIncomeFund.id)[0]?.amount ?? 0;
}
</script>
